import { useTranslation } from 'react-i18next';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteInfo } from '@/mocks/siteInfo';

const milestones = [
  { year: '2018', title: '品牌创立', desc: '韩漫之家正式成立，开始为中文读者提供韩国漫画在线阅读服务。' },
  { year: '2019', title: '内容扩展', desc: '漫画库扩充至3000+部，涵盖恋爱、动作、奇幻等多种分类的免费韩漫大全。' },
  { year: '2020', title: '用户突破', desc: '月活跃用户突破100万，成为国内领先的好看韩国漫画在线阅读平台。' },
  { year: '2021', title: '技术升级', desc: '全面升级阅读体验，优化漫画网址访问速度与移动端适配，实现高清流畅的韩漫在线观看。' },
  { year: '2022', title: '版权合作', desc: '与多家韩国漫画平台建立正式合作关系，为读者提供正版优质的漫画内容。' },
  { year: '2023', title: '社区建设', desc: '推出读者社区功能，打造互动交流平台，日访问量突破50万。' },
  { year: '2024', title: 'AI赋能', desc: '引入智能推荐系统，根据读者喜好精准推荐好看的韩国漫画。' },
  { year: '2025', title: '行业标杆', desc: '收录漫画超过12000部，成为中文互联网最受欢迎的韩漫阅读平台。' },
];

const testimonials = [
  {
    name: '王小明',
    role: '资深漫画读者',
    avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20young%20Asian%20man%2C%20friendly%20smile%2C%20clean%20studio%20background%2C%20soft%20natural%20lighting%2C%20corporate%20casual%20style&width=200&height=200&seq=avatar-01&orientation=squarish',
    text: '用韩漫之家两年多了，更新速度真的快，分类也很全。不管是恋爱甜宠还是热血动作，都能在这里找到心仪的漫画。强烈推荐给所有喜欢看韩漫的朋友！',
    rating: 5,
  },
  {
    name: '李雨桐',
    role: '大学生',
    avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20young%20Asian%20woman%2C%20warm%20smile%2C%20clean%20studio%20background%2C%20soft%20natural%20lighting%2C%20casual%20style&width=200&height=200&seq=avatar-02&orientation=squarish',
    text: '韩漫之家的界面很清爽，没有乱七八糟的广告。我最喜欢深夜看治愈系的漫画，一天的疲惫都消失了。这个漫画网址我收藏了，每天必刷！',
    rating: 5,
  },
  {
    name: '张浩然',
    role: '上班族',
    avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20Asian%20man%20in%2030s%2C%20confident%20expression%2C%20clean%20studio%20background%2C%20soft%20natural%20lighting%2C%20business%20casual&width=200&height=200&seq=avatar-03&orientation=squarish',
    text: '作为社畜，每天通勤路上看看韩漫是我最大的放松方式。韩漫之家资源是真的全，各种类型都有，而且手机上看也很方便。已经安利给全公司的同事了！',
    rating: 5,
  },
];

export default function AboutPage() {
  const { t } = useTranslation();

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '韩漫之家',
    url: 'https://www.hanmanzhijia.com',
    description: siteInfo.description,
    foundingDate: '2018',
    email: siteInfo.email,
    telephone: siteInfo.phone,
    address: {
      '@type': 'PostalAddress',
      streetAddress: '望京SOHO T2-A座 15层',
      addressLocality: '北京',
      addressRegion: '朝阳区',
      addressCountry: 'CN',
      postalCode: '100102',
    },
    sameAs: [
      siteInfo.socialLinks.weibo,
      siteInfo.socialLinks.bilibili,
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      telephone: siteInfo.phone,
      email: siteInfo.email,
      availableLanguage: ['Chinese'],
    },
  };

  return (
    <div className="min-h-screen pt-16">
      {/* Header */}
      <div className="bg-background-100/70 py-8">
        <div className="w-full max-w-[1280px] mx-auto">
          <Breadcrumb
            items={[
              { label: t('breadcrumb.home'), href: '/' },
              { label: t('about.title') },
            ]}
          />
          <div className="px-4 md:px-6 mt-4">
            <h1 className="text-2xl md:text-3xl font-heading text-foreground-900">
              {t('about.title')}
            </h1>
          </div>
        </div>
      </div>

      <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-8 md:py-12">
        {/* Brand Introduction */}
        <section className="mb-14 md:mb-20">
          <div className="text-center mb-10">
            <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-3">
              {t('about.introTitle')}
            </h2>
            <div className="w-12 h-1 bg-primary-500 rounded-full mx-auto mb-4"></div>
          </div>
          <div className="max-w-3xl mx-auto">
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
              {t('about.introContent')}
            </p>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
              作为免费的韩漫在线阅读网站，韩漫之家始终坚持用户至上的理念，不断优化阅读体验，丰富漫画资源库。我们与多家韩国原创漫画平台保持紧密合作，为读者提供正版、高清、及时更新的韩国漫画内容。无论您是喜欢恋爱甜宠、热血动作、奇幻冒险还是悬疑推理，都能在韩漫之家找到适合您的精彩作品。
            </p>
          </div>
        </section>

        {/* Timeline */}
        <section className="mb-14 md:mb-20">
          <div className="text-center mb-10">
            <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-3">
              发展历程
            </h2>
            <div className="w-12 h-1 bg-accent-500 rounded-full mx-auto mb-4"></div>
          </div>
          <div className="max-w-4xl mx-auto">
            <div className="relative">
              {/* Timeline Line */}
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 md:-translate-x-px"></div>
              <div className="space-y-8">
                {milestones.map((item, index) => (
                  <div
                    key={index}
                    className={`relative flex flex-col md:flex-row gap-4 md:gap-8 ${
                      index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    {/* Dot */}
                    <div className="absolute left-4 md:left-1/2 w-8 h-8 rounded-full bg-primary-500 border-4 border-background-50 -translate-x-1/2 z-10 flex items-center justify-center">
                      <span className="text-white text-[10px] font-bold">{item.year.slice(2)}</span>
                    </div>
                    {/* Content */}
                    <div className={`pl-14 md:pl-0 md:w-1/2 ${index % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'}`}>
                      <div className="bg-background-50 rounded-lg p-4 md:p-5 border border-background-200/70">
                        <span className="text-xs font-bold text-primary-500 mb-1 block">{item.year}</span>
                        <h4 className="text-sm font-semibold text-foreground-900 mb-1">{item.title}</h4>
                        <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Reputation / Testimonials */}
        <section className="mb-14 md:mb-20">
          <div className="text-center mb-10">
            <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-3">
              {t('about.reputationTitle')}
            </h2>
            <div className="w-12 h-1 bg-secondary-500 rounded-full mx-auto mb-4"></div>
            <p className="text-sm text-foreground-500 max-w-xl mx-auto">
              来自真实用户的好评与信赖，是我们不断前进的动力
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {testimonials.map((item, index) => (
              <div
                key={index}
                className="bg-background-50 rounded-lg p-5 md:p-6 border border-background-200/70"
              >
                {/* Rating Stars */}
                <div className="flex items-center gap-0.5 mb-4">
                  {Array.from({ length: item.rating }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-accent-400 text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  "{item.text}"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden shrink-0">
                    <img src={item.avatar} alt={item.name} className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">{item.name}</p>
                    <p className="text-xs text-foreground-500">{item.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Contact */}
        <section className="mb-10">
          <div className="text-center mb-10">
            <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-3">
              {t('about.contactTitle')}
            </h2>
            <div className="w-12 h-1 bg-primary-500 rounded-full mx-auto mb-4"></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            <div className="bg-background-50 rounded-lg p-5 border border-background-200/70 text-center">
              <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-50">
                <i className="ri-map-pin-line text-xl text-primary-500"></i>
              </div>
              <h4 className="text-sm font-semibold text-foreground-900 mb-1">{t('about.contactAddress')}</h4>
              <p className="text-xs text-foreground-500 leading-relaxed">{siteInfo.address}</p>
            </div>
            <div className="bg-background-50 rounded-lg p-5 border border-background-200/70 text-center">
              <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-50">
                <i className="ri-phone-line text-xl text-accent-500"></i>
              </div>
              <h4 className="text-sm font-semibold text-foreground-900 mb-1">{t('about.contactPhone')}</h4>
              <p className="text-xs text-foreground-500">{siteInfo.phone}</p>
            </div>
            <div className="bg-background-50 rounded-lg p-5 border border-background-200/70 text-center">
              <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-secondary-50">
                <i className="ri-mail-line text-xl text-secondary-500"></i>
              </div>
              <h4 className="text-sm font-semibold text-foreground-900 mb-1">{t('about.contactEmail')}</h4>
              <p className="text-xs text-foreground-500 break-all">{siteInfo.email}</p>
            </div>
            <div className="bg-background-50 rounded-lg p-5 border border-background-200/70 text-center">
              <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-50">
                <i className="ri-share-line text-xl text-primary-500"></i>
              </div>
              <h4 className="text-sm font-semibold text-foreground-900 mb-1">{t('about.contactSocial')}</h4>
              <div className="flex items-center justify-center gap-3 mt-2">
                <a href={siteInfo.socialLinks.weibo} target="_blank" rel="nofollow noopener noreferrer" className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center hover:bg-primary-100 transition-colors duration-200">
                  <i className="ri-weibo-line text-foreground-600"></i>
                </a>
                <a href={siteInfo.socialLinks.bilibili} target="_blank" rel="nofollow noopener noreferrer" className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center hover:bg-primary-100 transition-colors duration-200">
                  <i className="ri-bilibili-line text-foreground-600"></i>
                </a>
                <span className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center hover:bg-primary-100 transition-colors duration-200 cursor-pointer">
                  <i className="ri-wechat-line text-foreground-600"></i>
                </span>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(organizationSchema),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: '关于我们 - 韩漫之家',
            description: '了解韩漫之家品牌介绍、发展历程、用户口碑与联系方式。国内领先的免费韩国漫画在线阅读平台。',
            url: 'https://www.hanmanzhijia.com/about',
            isPartOf: {
              '@type': 'WebSite',
              name: '韩漫之家',
              url: 'https://www.hanmanzhijia.com',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '韩漫之家', item: 'https://www.hanmanzhijia.com/' },
              { '@type': 'ListItem', position: 2, name: '关于我们' },
            ],
          }),
        }}
      />
    </div>
  );
}